import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type { Receipt } from "~/managers/use-register-manager";
import type { CartItem } from "~/providers/cart-provider";
import type { OnlineOrder, Order } from "~/providers/order-provider";

interface OrderState {
  currentOnlineOrder?: OnlineOrder;
  currentOnlineOrderQueue: OnlineOrder[];
  addToCurrentOnlineOrderQueue: (order: OnlineOrder) => void;
  removeFromCurrentOnlineOrderQueue: (order: OnlineOrder) => void;
  setCurrentOnlineOrder: (order?: OnlineOrder) => void;
  currentOrder?: Order;
  setCurrentOrder: (order?: Order) => void;
  openOrdersOpen: boolean;
  setOpenOrdersOpen: (open: boolean) => void;
  checkoutOpen: boolean;
  setCheckoutOpen: (open: boolean) => void;
  transferDialogOpen: boolean;
  setTransferDialogOpen: (open: boolean) => void;
  splitDialogOpen: boolean;
  setSplitDialogOpen: (open: boolean) => void;
  transferItems: CartItem[];
  addTransferItem: (item: CartItem) => void;
  removeTransferItem: (item: CartItem) => void;
  setTransferItems: (items: CartItem[]) => void;
  receiptDialogOpen: boolean;
  setReceiptDialogOpen: (open: boolean) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  selectedReceipt?: {
    receipt?: Receipt;
    showCredit?: boolean;
  };
  setSelectedReceipt: ({
    receipt,
    showCredit,
  }: {
    receipt?: Receipt;
    showCredit?: boolean;
  }) => void;
  transferingItems: boolean;
  setTransferingItems: (transfering: boolean) => void;
}

export const useOrderStore = create<OrderState>()(
  devtools((set) => ({
    splitDialogOpen: false,
    currentOnlineOrderQueue: [],
    openOrdersOpen: false,
    receiptDialogOpen: false,
    checkoutOpen: false,
    transferDialogOpen: false,
    transferItems: [],
    isLoading: false,
    transferingItems: false,
    setIsLoading: (isLoading) => {
      set(() => ({ isLoading }));
    },
    addToCurrentOnlineOrderQueue: (newOrder) => {
      set((state) => ({
        currentOnlineOrderQueue: [
          ...state.currentOnlineOrderQueue.filter(
            (order) => order.id !== newOrder.id,
          ),
          newOrder,
        ],
      }));
    },
    removeFromCurrentOnlineOrderQueue: (order) => {
      set((state) => ({
        currentOnlineOrderQueue: state.currentOnlineOrderQueue.filter(
          (currentOrder) => currentOrder.id !== order.id,
        ),
        currentOnlineOrder:
          state.currentOnlineOrder?.id === order.id
            ? undefined
            : state.currentOnlineOrder,
      }));
    },
    setCurrentOnlineOrder: (order) => {
      set(() => ({ currentOnlineOrder: order }));
    },
    setSplitDialogOpen: (open) => {
      set(() => ({ splitDialogOpen: open }));
    },
    setTransferingItems(transfering) {
      set(() => ({ transferingItems: transfering }));
    },
    setSelectedReceipt(receipt) {
      set(() => ({ selectedReceipt: receipt }));
    },
    setReceiptDialogOpen(open) {
      set(() => ({ receiptDialogOpen: open }));
    },
    addTransferItem: (item: CartItem) => {
      set((state) => ({
        transferItems: [...state.transferItems, item],
      }));
    },
    removeTransferItem: (item: CartItem) => {
      set((state) => {
        const index = state.transferItems.findIndex(
          (i) => i.reference === item.reference,
        );
        if (index === -1) return state;
        return {
          transferItems: [
            ...state.transferItems.slice(0, index),
            ...state.transferItems.slice(index + 1),
          ],
        };
      });
    },
    setTransferItems: (items: CartItem[]) => {
      set(() => ({
        transferItems: items,
      }));
    },
    setCheckoutOpen: (open) => {
      set(() => ({ checkoutOpen: open }));
    },
    setTransferDialogOpen: (open: boolean) => {
      set({ transferDialogOpen: open });
    },
    setOpenOrdersOpen: (open) => {
      set(() => ({ openOrdersOpen: open }));
    },
    setCurrentOrder: (order) => {
      set(() => ({ currentOrder: order }));
    },
  })),
);
